(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DSProductModel = api;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';

  const CATEGORIES = {
    rings: { label: 'Engagement rings', singular: 'ring', isRing: true, collectionHandle: 'lab-grown-diamond-rings' },
    bridal: { label: 'Bridal sets', singular: 'bridal set', isRing: true, collectionHandle: 'lab-grown-bridal-sets' },
    bands: { label: 'Wedding & fashion bands', singular: 'band', isRing: true, collectionHandle: 'lab-grown-diamond-fashion-bands' },
    earrings: { label: 'Earrings', singular: 'earring', isRing: false, collectionHandle: 'lab-grown-diamond-earrings' },
    necklaces: { label: 'Necklaces', singular: 'necklace', isRing: false, collectionHandle: 'lab-grown-diamond-necklaces' },
    bracelets: { label: 'Bracelets', singular: 'bracelet', isRing: false, collectionHandle: 'lab-grown-diamond-bracelets' },
    jewelry: { label: 'Jewelry', singular: 'jewelry', isRing: false, collectionHandle: 'all' }
  };

  function readable(input) {
    if (input == null) return '';
    if (typeof input === 'object') {
      const fields = Array.isArray(input.fields) ? input.fields : [];
      const label = fields.find(field => field.key === 'label');
      return readable(input.label ?? label?.value ?? input.displayName ?? input.jsonValue ?? input.value);
    }
    if (!['string', 'number'].includes(typeof input)) return '';
    const value = String(input).trim();
    return /^(?:n\s*[\/\\]?\s*a|none|null|undefined|not applicable|unknown|[-–—]+)$/i.test(value) ? '' : value;
  }

  function normalize(input) {
    return readable(input).toLowerCase().replace(/[_–—-]+/g, ' ').replace(/\s+/g, ' ');
  }

  function categoryHint(input, ringFamilyOnly) {
    const text = normalize(input);
    if (!ringFamilyOnly) {
      if (/\bearrings?\b/.test(text)) return 'earrings';
      if (/\b(?:necklaces?|pendants?)\b/.test(text)) return 'necklaces';
      if (/\b(?:bracelets?|bangles?)\b/.test(text)) return 'bracelets';
    }
    if (/\bbridal\b|\bmatching (?:wedding )?(?:ring )?sets?\b/.test(text)) return 'bridal';
    if (/\bbands?\b/.test(text)) return 'bands';
    if (/\brings?\b/.test(text)) return 'rings';
    return '';
  }

  function classify(product) {
    const taxonomy = product.category || {};
    const explicit = categoryHint([taxonomy.name, taxonomy.fullName].filter(Boolean).join(' '));
    if (['earrings', 'necklaces', 'bracelets', 'bridal', 'bands'].includes(explicit)) return explicit;
    const sources = [Array.isArray(product.tags) ? product.tags.join(' ') : product.tags, product.handle, product.type || product.productType, product.title];
    // Broad collections contain unrelated categories and are deliberately not classification evidence.
    if (explicit === 'rings') {
      for (const source of sources) {
        const hint = categoryHint(source, true);
        if (hint === 'bridal' || hint === 'bands') return hint;
      }
      return 'rings';
    }
    for (const source of sources) {
      const hint = categoryHint(source);
      if (hint) return hint;
    }
    return 'jewelry';
  }

  function build(input) {
    const config = input || {};
    const product = config.productData || {};
    const metafields = config.metafields || {};
    const id = classify(product);
    const category = { id, ...CATEGORIES[id] };
    const collections = Array.isArray(config.collections) ? config.collections : [];
    const suppliedMarketing = (config.categoryMarketing || {})[id] || (config.categoryMarketing || {}).jewelry || null;
    const collectionCandidates = collections.filter(collection => {
      const handle = readable(collection.handle);
      if (!handle || ['all', 'bridal-sets', 'classic-rings'].includes(handle)) return false;
      return categoryHint(handle) === id;
    });
    const contextHandle = readable(typeof config.collectionContext === 'string' ? config.collectionContext : config.collectionContext?.handle);
    const plannedHandle = readable(suppliedMarketing?.collectionHandle || suppliedMarketing?.handle);
    const selectedCollection = collectionCandidates.find(collection => collection.handle === contextHandle)
      || collectionCandidates.find(collection => collection.handle === plannedHandle)
      || collectionCandidates.slice().sort((a, b) => a.handle.length - b.handle.length)[0];
    category.collectionHandle = selectedCollection?.handle || 'all';
    const issues = [];
    const groups = [
      { id: 'product', label: 'Product details', items: [] },
      { id: 'diamond', label: 'Diamond information', items: [] },
      { id: 'side-diamonds', label: 'Side diamond information', items: [] },
      { id: 'attributes', label: 'Additional details', items: [] }
    ];
    const byGroup = Object.fromEntries(groups.map(group => [group.id, group]));
    const byKey = new Map();
    const issue = (code, key, message, value) => issues.push({ code, key, message, ...(value === undefined ? {} : { value }) });

    function value(key) {
      const result = readable(metafields[key]);
      if (/gid:\/\/shopify\//i.test(result)) {
        issue('UNRESOLVED_REFERENCE', key, 'A Shopify reference has no resolved display label and was omitted.', result);
        return '';
      }
      return result;
    }

    function add(group, key, label, result) {
      if (!result) return;
      const item = { key, label, value: result };
      byGroup[group].items.push(item);
      byKey.set(key, item);
    }

    const carats = result => /^\d+(?:\.\d+)?$/.test(result) ? result + ' ct' : result;
    function shape(key) {
      const result = value(key);
      if (/^[+-]?\d+(?:[.,]\d+)?(?:\s*(?:ct|ctw|tcw|carats?))?$/i.test(result)) {
        issue('MALFORMED_DIAMOND_SHAPE', key, 'A numeric weight is stored as a diamond shape; the shape was omitted without guessing a replacement.', result);
        return '';
      }
      return result;
    }

    const styleLabel = category.singular.charAt(0).toUpperCase() + category.singular.slice(1) + ' style';
    add('product', 'ring_style', styleLabel, value('ring_style'));
    add('product', 'total_carat_weight', 'Total diamond weight', carats(value('total_carat_weight')));
    add('product', 'length', 'Length', value('length'));
    add('product', 'metal_weight', 'Metal weight', value('metal_weight'));

    add('diamond', 'main_diamond_shape', 'Main diamond shape', shape('main_diamond_shape'));
    add('diamond', 'main_diamond_carat_weight', category.id === 'rings' ? 'Main diamond weight' : 'Main diamond weight (listed)', carats(value('main_diamond_carat_weight')));
    add('diamond', 'main_diamond_color', 'Main diamond color', value('main_diamond_color'));
    add('diamond', 'main_diamond_clarity', 'Main diamond clarity', value('main_diamond_clarity'));
    add('diamond', 'main_diamond_cut', 'Diamond cut grade', value('main_diamond_cut'));
    add('diamond', 'main_stone_type', 'Main stone type', value('main_stone_type'));
    add('diamond', 'certification', 'Certification', value('certification'));
    add('diamond', 'treatments', 'Treatments', value('treatments'));

    const listedMainWeight = value('main_diamond_carat_weight');
    const listedTotalWeight = value('total_carat_weight');
    if (/\b(?:ctw|tcw)\b/i.test(listedMainWeight)) {
      const mainNumber = listedMainWeight.match(/^\d+(?:\.\d+)?/);
      const totalNumber = listedTotalWeight.match(/^\d+(?:\.\d+)?/);
      if (mainNumber && totalNumber && Math.abs(Number(mainNumber[0]) - Number(totalNumber[0])) > 0.000001) {
        issue('DIFFERING_LISTED_TOTAL_WEIGHTS', 'main_diamond_carat_weight', 'The main-weight field includes a total-weight unit and differs from total_carat_weight. Both original values are retained for review.', listedMainWeight + ' / ' + listedTotalWeight);
      }
    }

    const sideWeight = value('side_diamonds_weight');
    const sideShape = shape('side_diamonds_shape');
    const explicitlyNoSides = /^0+(?:\.0+)?(?:\s*(?:ct|ctw|tcw|carats?))?$/i.test(sideWeight);
    if (!explicitlyNoSides) {
      add('side-diamonds', 'side_diamonds_weight', 'Side diamond weight', carats(sideWeight));
      add('side-diamonds', 'side_diamonds_shape', 'Side diamond shape', sideShape);
      add('side-diamonds', 'side_diamonds_color', 'Side diamond color', value('side_diamonds_color'));
      add('side-diamonds', 'side_diamonds_clarity', 'Side diamond clarity', value('side_diamonds_clarity'));
      // The store's diamond_type definition is specifically named Side Diamond Type.
      if (byGroup['side-diamonds'].items.length) add('side-diamonds', 'diamond_type', 'Side diamond type', value('diamond_type'));
    }

    const appraisal = value('appraisal_value');
    if (appraisal) {
      const sourceCurrency = readable(config.appraisalCurrency || metafields.appraisal_currency).toUpperCase();
      if (/^[A-Z]{3}$/.test(sourceCurrency)) add('product', 'appraisal_value', 'Appraisal value', appraisal + ' ' + sourceCurrency);
      else issue('APPRAISAL_CURRENCY_UNKNOWN', 'appraisal_value', 'Appraisal omitted because its source currency is unknown; the current shopping currency is not a source currency.', appraisal);
    }

    for (const attribute of Array.isArray(config.taxonomyAttributes) ? config.taxonomyAttributes : []) {
      const key = readable(attribute.key);
      if (!key) continue;
      const resolved = [];
      for (const raw of Array.isArray(attribute.values) ? attribute.values : [attribute.values]) {
        const text = readable(raw);
        if (/gid:\/\/shopify\//i.test(text)) issue('UNRESOLVED_TAXONOMY_REFERENCE', key, 'Taxonomy reference omitted until its metaobject display label is resolved.', text);
        else if (text && !resolved.includes(text)) resolved.push(text);
      }
      const label = readable(attribute.label) || key.replace(/[-_]/g, ' ').replace(/^./, letter => letter.toUpperCase());
      const customStyle = normalize(value('ring_style'));
      if (/^(?:bracelet|earring|necklace|ring)[-_]design$/.test(key) && customStyle && resolved.length) {
        const conflicting = resolved.filter(text => {
          const design = normalize(text);
          return !customStyle.includes(design) && !design.includes(customStyle);
        });
        if (conflicting.length) issue('CONFLICTING_TAXONOMY_DESIGN', key, 'Taxonomy design differs from the product-specific style. The custom style is displayed and the taxonomy design is omitted.', conflicting.join(', '));
        continue;
      }
      add('attributes', key, key === 'color-pattern' ? 'Color / pattern' : label, resolved.join(', '));
    }

    const preferred = category.id === 'rings' || category.id === 'bridal'
      ? ['main_diamond_shape', 'main_diamond_carat_weight', 'total_carat_weight', 'main_diamond_clarity', 'ring_style', 'certification']
      : ['total_carat_weight', 'ring_style', 'main_diamond_shape', 'length', 'main_diamond_clarity', 'main_diamond_carat_weight', 'certification'];
    const summary = preferred.map(key => byKey.get(key)).filter(Boolean).slice(0, 4);
    let marketing = plannedHandle && plannedHandle === category.collectionHandle ? suppliedMarketing : null;
    if (!marketing && selectedCollection?.image?.src) marketing = {
      collectionHandle: category.collectionHandle,
      collectionTitle: readable(selectedCollection.title) || category.label,
      hero: {
        src: selectedCollection.image.src,
        alt: readable(selectedCollection.image.alt) || readable(selectedCollection.title) || category.label,
        heading: (readable(selectedCollection.title) || category.label) + ' collection',
        description: 'Explore more from this Diamond Studio collection.'
      }
    };
    return {
      category,
      title: readable(product.title),
      fields: groups.filter(group => group.items.length),
      summary,
      issues,
      nativeOptions: Array.isArray(config.options) ? config.options : [],
      variants: Array.isArray(config.variants) ? config.variants : [],
      marketing,
      collections
    };
  }

  return { build };
});
